import React from "react";
import { Link as RouterLink } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  Link,
  MenuItem,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { getOrParseDateTime, salesRepDisplayName } from "@cpt/shared";
import useAsyncAbortableRequest from "hooks/useAsyncAbortableRequest";
import { formatDateOnly } from "utils/formatters/DateFormatters";
import { ordersApi, plansApi } from "client/api-v2";
import { OrderPaymentTerm, OrderStatus, Tier } from "client/api-v2";
import type { OrderDto, Plan, PreapprovedLegalTermDto, ProductPlatform, SpecialLicenceTermDto } from "client/api-v2";
import DatePickerInput from "components/DatePickerInput";
import { FieldLabel } from "components/FieldLabel/FieldLabel";
import { EmptyValue, MutedPlaceholder } from "components/MutedPlaceholder";
import { BuilderSection } from "areas/orders/builder/BuilderSection";
import { isBlankSpecialLicenceTerm } from "areas/orders/builder/useOrderDraft";
import { ViaResellerBadge } from "areas/orders/components/chrome/ViaResellerBadge";
import { useGuardedNavigate } from "areas/orders/hooks/useUnsavedChangesGuard";
import { withReturnToOrder } from "areas/orders/order-routes";
import type { UsageBasis } from "areas/orders/orderConstants";
import {
  orderPaymentTermLabels,
  productPlatformLabels,
  selfServeLabel,
  usageBasisPlanNames,
} from "areas/orders/orderConstants";
import { listPreapprovedLegalTerms } from "areas/orders/services/orderService";
import {
  derivedTermEnd,
  earliestTermStart,
  latestQuoteExpiry,
  latestTermStart,
  quoteExpiryProblem,
  termLengthMonths,
  termStartProblem,
  toIsoDate,
  todayUtc,
} from "areas/orders/utils/orderDateLimits";
import { orderStatusLabel } from "areas/orders/utils/orderStatusLabel";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";
import type { SectionProps } from "./SectionProps";

export type CommercialTermsSectionProps = SectionProps & {
  showTermProblems: boolean;
};

const quoteExpiryDays = 30;
const quoteExpiryQuickSetDays = [30, 60];

const customAgreementReferenceMaxLength = 255;

function characterCount(length: number, max: number): string {
  return `${length.toLocaleString("en-US")}/${max.toLocaleString("en-US")} characters`;
}

const purchaseOrderNumberMaxLength = 100;

const customAgreementReferenceLabel = "Custom agreement reference";
const customAgreementReferencePlaceholder = "Enter the agreement reference";
const customAgreementReferenceNote = "The quote prints a term that names this reference.";

const termStartRequired = "A term start date is required.";

const specialLicenceTermTitleMaxLength = 200;
const specialLicenceTermTextMaxLength = 4000;

const incompleteSpecialLicenceTerm = "A special license term needs both a title and its text.";

export function validateSpecialLicenceTerms(terms: readonly SpecialLicenceTermDto[]): string | true {
  const isComplete = (term: SpecialLicenceTermDto) => term.title.trim() !== "" && term.text.trim() !== "";
  return terms.every(isComplete) || incompleteSpecialLicenceTerm;
}

const collapsedPreviewRows = 4;
const longPreviewCharacters = collapsedPreviewRows * 80;

const legalTermsUnavailable = "The pre-approved legal terms catalog could not be read, so the selection cannot change.";

const plansUnavailable = "The plan list could not be read, so the usage basis cannot change.";

const salesRepsUnavailable = "The representative list could not be read, so the rep cannot change.";

const selfServeValue = "";

function toPaymentTerm(value: string): OrderPaymentTerm | undefined {
  return Object.values(OrderPaymentTerm).find((term) => term === value);
}

async function loadSalesReps(signal: AbortSignal): Promise<string[]> {
  return (await ordersApi.getV2OrdersSalesReps({ signal })).data;
}

function loadLegalTerms(): Promise<PreapprovedLegalTermDto[]> {
  return listPreapprovedLegalTerms(true);
}

async function loadPlans(platform: ProductPlatform, signal: AbortSignal): Promise<Plan[]> {
  return (await plansApi.getV2Plans({ query: { platform, page: 1, pageSize: 100 }, signal })).data.items;
}

function isoDateInDays(days: number): string {
  return todayUtc().plus({ days }).toISODate() ?? "";
}

function daysUntil(isoDate: string): number {
  return Math.round(getOrParseDateTime(isoDate).startOf("day").diff(todayUtc(), "days").days);
}

function dayCount(days: number): string {
  return `${days} ${days === 1 ? "day" : "days"}`;
}

function noLongerExpires(status: OrderStatus): string {
  return status === OrderStatus.Expired
    ? "No longer expires — this quote has expired."
    : `No longer expires — this order is ${orderStatusLabel(status)}.`;
}

function describeQuoteExpiry(isoDate: string, status: OrderStatus): string {
  if (status === OrderStatus.Cancelled || status === OrderStatus.Accepted) {
    return noLongerExpires(status);
  }

  const days = daysUntil(isoDate);
  if (days < 0) {
    return `Expired ${dayCount(-days)} ago`;
  }
  if (status === OrderStatus.Expired) {
    return noLongerExpires(status);
  }
  return days === 0 ? "Expires today" : `Expires in ${dayCount(days)}`;
}

function termEndDisplayText(termStartDate: string): string {
  const isoDate = derivedTermEnd(termStartDate);
  return isoDate === "" ? "" : formatDateOnly(getOrParseDateTime(isoDate));
}

function DateValue({ isoDate }: { isoDate: string | null }) {
  return isoDate === null || isoDate === "" ? <EmptyValue /> : <>{formatDateOnly(getOrParseDateTime(isoDate))}</>;
}

function ReadOnlyField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div" sx={{ fontWeight: 600 }}>
        {children}
      </Typography>
    </Box>
  );
}

function FieldNote({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="caption" component="div" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography variant="body2" sx={{ color: "text.secondary" }}>
      {children}
    </Typography>
  );
}

function AccountPanel({ order }: { order: OrderDto }) {
  const guardedNavigate = useGuardedNavigate();
  const subscriptionPath = withReturnToOrder(subscriptionRoutes.detail(order.subscriptionId).root, order.odNumber);

  return (
    <Stack spacing={0.5}>
      <Typography variant="caption" sx={{ color: "text.secondary" }}>
        Billing account
      </Typography>
      <Stack direction="row" spacing={0.75} sx={{ alignItems: "center" }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {order.billingAccountLegalName}
        </Typography>
        {order.isViaReseller && (
          <ViaResellerBadge resellerLegalName={order.resellerBillingAccountLegalName ?? undefined} />
        )}
      </Stack>
      <FieldNote>
        {order.subscriptionSerial === null ? (
          <MutedPlaceholder>License key: not issued yet</MutedPlaceholder>
        ) : (
          <Box component="span" sx={{ fontFamily: "monospace" }}>
            {`License key: ${order.subscriptionSerial}`}
          </Box>
        )}
      </FieldNote>
      <FieldNote>
        {"Subscription: "}
        <Link component={RouterLink} to={subscriptionPath} onClick={guardedNavigate(subscriptionPath)}>
          {order.subscriptionName ?? order.subscriptionShortId}
        </Link>
      </FieldNote>
      <FieldNote>{`Current Plan: ${order.currentPlanName}`}</FieldNote>
    </Stack>
  );
}

function specificExpiryStartingPoint(validUntil: string | null): string {
  const inRange = validUntil !== null && quoteExpiryProblem({ validUntilOverride: validUntil }) === undefined;
  return inRange ? validUntil : isoDateInDays(quoteExpiryDays);
}

function QuoteExpiryFields({ order, draft, setField, readOnly }: SectionProps) {
  const isSpecific = draft.validUntilOverride !== null;
  const override = toIsoDate(draft.validUntilOverride) ?? "";
  const validUntil = toIsoDate(order.validUntil);
  const overrideProblem = readOnly ? undefined : quoteExpiryProblem(draft);

  return (
    <Stack spacing={1.5} useFlexGap>
      <FormControl>
        <FieldLabel id="quote-expiry-mode">Quote expiry</FieldLabel>
        <RadioGroup
          row
          aria-labelledby="quote-expiry-mode"
          value={isSpecific ? "specific" : "standard"}
          onChange={(event) =>
            setField(
              "validUntilOverride",
              event.target.value === "specific" ? specificExpiryStartingPoint(validUntil) : null
            )
          }
        >
          <FormControlLabel
            value="standard"
            control={<Radio />}
            label={`${quoteExpiryDays} days from publish`}
            disabled={readOnly}
          />
          <FormControlLabel value="specific" control={<Radio />} label="Specific date" disabled={readOnly} />
        </RadioGroup>
      </FormControl>

      {isSpecific && (
        <Stack spacing={1} sx={{ mt: 1.5 }}>
          <Stack spacing={0.5}>
            <FieldLabel id="valid-until-label" htmlFor="valid-until" size="small">
              Valid until (UTC)
            </FieldLabel>
            <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
              <Box sx={{ maxWidth: 200, width: "100%" }}>
                <DatePickerInput
                  id="valid-until"
                  labelledBy="valid-until-label"
                  label="valid until"
                  hideLabel
                  size="small"
                  value={override}
                  onChange={(date) => setField("validUntilOverride", date?.toISODate() ?? "")}
                  minDate={todayUtc()}
                  maxDate={getOrParseDateTime(latestQuoteExpiry())}
                  isInvalid={overrideProblem !== undefined}
                  errorMessage={overrideProblem}
                  disabled={readOnly}
                  showLocalConversion={false}
                  variant="outlined"
                />
              </Box>
              {override && <FieldNote>{describeQuoteExpiry(override, order.status)}</FieldNote>}
            </Stack>
          </Stack>
          {!readOnly && (
            <Stack direction="row" spacing={2}>
              {quoteExpiryQuickSetDays.map((days) => (
                <Button key={days} size="small" onClick={() => setField("validUntilOverride", isoDateInDays(days))}>
                  {`in ${days} days`}
                </Button>
              ))}
            </Stack>
          )}
        </Stack>
      )}

      {!isSpecific && validUntil !== null && (
        <Stack direction="row" spacing={2} sx={{ alignItems: "center" }}>
          <ReadOnlyField label="Valid until (UTC)">
            <DateValue isoDate={validUntil} />
          </ReadOnlyField>
          <FieldNote>{describeQuoteExpiry(validUntil, order.status)}</FieldNote>
        </Stack>
      )}
    </Stack>
  );
}

function usageBasisOf(plan: Plan | undefined): UsageBasis | null {
  if (plan?.tier !== Tier.Platform) {
    return null;
  }
  if (plan.name === usageBasisPlanNames.Forecast) {
    return "Forecast";
  }
  return plan.name === usageBasisPlanNames.Actual ? "Actual" : null;
}

function SubscriptionFields({ order, draft, setField, readOnly }: SectionProps) {
  const { data: plans, error: plansError } = useAsyncAbortableRequest(loadPlans, order.platform);
  const termStartError =
    draft.termStartDate === "" ? termStartRequired : readOnly ? undefined : termStartProblem(draft, order);

  const usageBasisPlans = React.useMemo(() => {
    const current = (plans ?? []).filter((candidate) => !candidate.isSuperseded && candidate.tier === Tier.Platform);
    const planFor = (basis: UsageBasis) => current.find((candidate) => candidate.name === usageBasisPlanNames[basis]);
    return { Actual: planFor("Actual"), Forecast: planFor("Forecast") };
  }, [plans]);

  const draftPlan = plans?.find((candidate) => candidate.id === draft.planId);
  const usageBasis = usageBasisOf(draftPlan);
  const usageBasisDisabled = readOnly || plansError !== undefined;

  return (
    <Stack spacing={1.5}>
      <SectionHeading>Subscription</SectionHeading>
      <Grid container rowSpacing={2} columnSpacing={5}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Grid container spacing={2}>
            <Grid size={6}>
              <Stack spacing={0.5}>
                <FieldLabel id="licence-type-label" size="small">
                  License type
                </FieldLabel>
                <TextField
                  id="licence-type"
                  select
                  size="small"
                  fullWidth
                  disabled
                  value={order.platform}
                  slotProps={{ select: { readOnly: true, labelId: "licence-type-label" } }}
                >
                  <MenuItem value={order.platform}>{productPlatformLabels[order.platform]}</MenuItem>
                </TextField>
              </Stack>
            </Grid>
            <Grid size={6}>
              <Stack spacing={0.5}>
                <FieldLabel id="usage-basis-label" size="small">
                  Usage basis
                </FieldLabel>
                <ToggleButtonGroup
                  exclusive
                  size="small"
                  fullWidth
                  aria-labelledby="usage-basis-label"
                  value={usageBasis}
                  onChange={(_, selected: UsageBasis | null) => {
                    const selectedPlan = selected === null ? undefined : usageBasisPlans[selected];
                    if (selectedPlan !== undefined) {
                      setField("planId", selectedPlan.id);
                    }
                  }}
                >
                  {(["Actual", "Forecast"] as const).map((basis) => (
                    <ToggleButton
                      key={basis}
                      value={basis}
                      disabled={usageBasisDisabled || usageBasisPlans[basis] === undefined}
                    >
                      {basis}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
                {plansError !== undefined && <FieldNote>{plansUnavailable}</FieldNote>}
                {plans !== undefined && usageBasis === null && <FieldNote>{`Plan: ${order.planName}`}</FieldNote>}
              </Stack>
            </Grid>
          </Grid>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Stack spacing={0.5}>
                  <FieldLabel htmlFor="term-length" size="small">
                    Term length
                  </FieldLabel>
                  <TextField
                    id="term-length"
                    size="small"
                    fullWidth
                    disabled
                    value={`${termLengthMonths} months`}
                    slotProps={{ input: { readOnly: true } }}
                  />
                </Stack>
              </Grid>
            </Grid>
            <Grid container spacing={2}>
              <Grid size={6}>
                <Stack spacing={0.5}>
                  <FieldLabel id="term-start-date-label" htmlFor="term-start-date" size="small">
                    Term start (UTC)
                  </FieldLabel>
                  <DatePickerInput
                    id="term-start-date"
                    labelledBy="term-start-date-label"
                    label="term start"
                    hideLabel
                    size="small"
                    value={draft.termStartDate}
                    onChange={(date) => setField("termStartDate", date?.toISODate() ?? "")}
                    minDate={getOrParseDateTime(earliestTermStart())}
                    maxDate={getOrParseDateTime(latestTermStart(order))}
                    referenceDate={todayUtc()}
                    disabled={readOnly}
                    isInvalid={termStartError !== undefined}
                    errorMessage={termStartError}
                    showLocalConversion={false}
                    variant="outlined"
                  />
                </Stack>
              </Grid>
              <Grid size={6}>
                <Stack spacing={0.5}>
                  <FieldLabel htmlFor="term-end" size="small">
                    Term end (derived, UTC)
                  </FieldLabel>
                  <TextField
                    id="term-end"
                    size="small"
                    fullWidth
                    disabled
                    value={termEndDisplayText(draft.termStartDate)}
                    slotProps={{ input: { readOnly: true } }}
                  />
                </Stack>
              </Grid>
            </Grid>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

function PurchaseOrderFields({ order, draft, setField, readOnly }: SectionProps) {
  const purchaseOrderNumber = draft.purchaseOrderNumber ?? "";
  const onChange = (value: string) => setField("purchaseOrderNumber", value);

  if (draft.resellerBillingAccountCode === null) {
    return (
      <Stack spacing={0.5}>
        <FieldLabel htmlFor="purchase-order-number">PO number</FieldLabel>
        <TextField
          id="purchase-order-number"
          size="small"
          value={purchaseOrderNumber}
          onChange={(event) => onChange(event.target.value)}
          helperText={characterCount(purchaseOrderNumber.length, purchaseOrderNumberMaxLength)}
          disabled={readOnly}
          slotProps={{ htmlInput: { maxLength: purchaseOrderNumberMaxLength } }}
          sx={{ maxWidth: 200 }}
        />
      </Stack>
    );
  }

  const resellerName =
    draft.resellerBillingAccountCode === order.resellerBillingAccountCode
      ? order.resellerBillingAccountLegalName ?? draft.resellerBillingAccountCode
      : draft.resellerBillingAccountCode;

  return (
    <Stack spacing={1.5}>
      <Stack spacing={0.5}>
        <SectionHeading>Reseller</SectionHeading>
        <FieldNote>A different party is billed and fulfills the order. The end customer stays on the order.</FieldNote>
      </Stack>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 5 }}>
          <ReadOnlyField label="Reseller (billing party)">{resellerName}</ReadOnlyField>
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <TextField
            size="small"
            fullWidth
            label="Reseller PO number"
            value={purchaseOrderNumber}
            onChange={(event) => onChange(event.target.value)}
            helperText={characterCount(purchaseOrderNumber.length, purchaseOrderNumberMaxLength)}
            disabled={readOnly}
            slotProps={{ htmlInput: { maxLength: purchaseOrderNumberMaxLength } }}
          />
        </Grid>
      </Grid>
    </Stack>
  );
}

function SpecialLicenceTermsFields({
  draft,
  setField,
  readOnly,
  showTermProblems,
}: Omit<CommercialTermsSectionProps, "order">) {
  const terms = draft.specialLicenceTerms;

  const [visitedRows, setVisitedRows] = React.useState<ReadonlySet<number>>(() => new Set());
  const visitRow = (index: number) => setVisitedRows((current) => new Set(current).add(index));

  const removeRow = (removed: number) => {
    setField(
      "specialLicenceTerms",
      terms.filter((_, position) => position !== removed)
    );
    // Why: a term has no id, so marks are keyed by position and shift up with the rows below.
    setVisitedRows(
      (current) =>
        new Set(
          [...current]
            .filter((position) => position !== removed)
            .map((position) => (position > removed ? position - 1 : position))
        )
    );
  };

  const editTerm = (index: number, changes: Partial<SpecialLicenceTermDto>) =>
    setField(
      "specialLicenceTerms",
      terms.map((term, position) => (position === index ? { ...term, ...changes } : term))
    );

  return (
    <Stack spacing={1.5} sx={{ mt: "12px" }}>
      <Box>
        <SectionHeading>Special license terms</SectionHeading>
        <FieldNote>Any term here holds the order for staff confirmation before billing setup can start.</FieldNote>
      </Box>

      {terms.length === 0 && (
        <FieldNote>
          <MutedPlaceholder>None</MutedPlaceholder>
        </FieldNote>
      )}

      {terms.map((term, index) => {
        const number = index + 1;
        const titleMissing = term.title.trim() === "";
        const textMissing = term.text.trim() === "";
        const statesItsRule = !isBlankSpecialLicenceTerm(term) || visitedRows.has(index) || showTermProblems;

        return (
          <Box
            key={index}
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gridTemplateAreas: `"titleLabel ." "title remove" "textLabel ." "text ."`,
              columnGap: readOnly ? 0 : 1,
              rowGap: 0.5,
            }}
          >
            <FieldLabel htmlFor={`special-licence-term-${index}-title`} size="small" sx={{ gridArea: "titleLabel" }}>
              {`Term ${number} title`}
            </FieldLabel>
            <TextField
              id={`special-licence-term-${index}-title`}
              size="small"
              fullWidth
              value={term.title}
              error={titleMissing && statesItsRule}
              onChange={(event) => editTerm(index, { title: event.target.value })}
              onBlur={() => visitRow(index)}
              helperText={characterCount(term.title.length, specialLicenceTermTitleMaxLength)}
              disabled={readOnly}
              slotProps={{ htmlInput: { maxLength: specialLicenceTermTitleMaxLength } }}
              sx={{ gridArea: "title" }}
            />
            {!readOnly && (
              <IconButton
                aria-label={`Remove term ${number}`}
                onClick={() => removeRow(index)}
                sx={{ gridArea: "remove", alignSelf: "start" }}
              >
                <DeleteOutlinedIcon fontSize="small" />
              </IconButton>
            )}
            <FieldLabel
              htmlFor={`special-licence-term-${index}-text`}
              size="small"
              sx={{ gridArea: "textLabel", mt: 0.5 }}
            >
              {`Term ${number} text`}
            </FieldLabel>
            <TextField
              id={`special-licence-term-${index}-text`}
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={term.text}
              error={textMissing && statesItsRule}
              helperText={
                (titleMissing || textMissing) && statesItsRule
                  ? incompleteSpecialLicenceTerm
                  : characterCount(term.text.length, specialLicenceTermTextMaxLength)
              }
              onChange={(event) => editTerm(index, { text: event.target.value })}
              onBlur={() => visitRow(index)}
              disabled={readOnly}
              slotProps={{ htmlInput: { maxLength: specialLicenceTermTextMaxLength } }}
              sx={{ gridArea: "text" }}
            />
          </Box>
        );
      })}

      {!readOnly && (
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => setField("specialLicenceTerms", [...terms, { title: "", text: "" }])}
          sx={{ alignSelf: "flex-start" }}
        >
          Add special license term
        </Button>
      )}
    </Stack>
  );
}

function LegalTermsFields({ draft, setField, readOnly, showTermProblems }: Omit<CommercialTermsSectionProps, "order">) {
  const { data: catalogue, error: catalogueError } = useAsyncAbortableRequest(loadLegalTerms);
  const [previewExpanded, setPreviewExpanded] = React.useState(false);

  const catalogueUnavailable = catalogueError !== undefined;
  const terms = catalogue ?? [];

  const termById = (id: string): PreapprovedLegalTermDto =>
    terms.find((term) => term.id === id) ?? { id, name: id, text: "", sortOrder: 0, isAvailable: false };

  const selected = draft.preapprovedLegalTermIds.map(termById);

  const options = [...terms.filter((term) => term.isAvailable), ...selected.filter((term) => !term.isAvailable)].sort(
    (left, right) => left.sortOrder - right.sortOrder
  );

  const customAgreementAttached = draft.customAgreementReference !== null;

  const previewParagraphs = [
    ...selected.map((term) => term.text).filter((text) => text !== ""),
    ...draft.specialLicenceTerms
      .filter((term) => term.title.trim() !== "" && term.text.trim() !== "")
      .map((term) => `${term.title}: ${term.text}`),
  ];
  const previewText = previewParagraphs.length === 0 ? "No legal terms selected." : previewParagraphs.join("\n\n");
  const previewIsLong = previewParagraphs.length > 1 || previewText.length > longPreviewCharacters;

  return (
    <Stack spacing={2}>
      <SectionHeading>Legal terms</SectionHeading>
      <Grid container rowSpacing={4} columnSpacing={5}>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={2} useFlexGap>
            <Stack spacing={0.5}>
              <FieldLabel id="preapproved-legal-terms-label" htmlFor="preapproved-legal-terms" size="small">
                Preapproved legal terms
              </FieldLabel>
              <Autocomplete
                id="preapproved-legal-terms"
                multiple
                size="small"
                disabled={readOnly || catalogueUnavailable}
                options={options}
                value={selected}
                getOptionLabel={(term) => term.name}
                getOptionDisabled={(term) => !term.isAvailable}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                onChange={(_, chosen) =>
                  setField(
                    "preapprovedLegalTermIds",
                    chosen.map((term) => term.id)
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={selected.length === 0 ? "Select terms…" : undefined}
                    helperText={catalogueUnavailable ? legalTermsUnavailable : undefined}
                  />
                )}
              />
            </Stack>
            <Box>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={customAgreementAttached}
                    disabled={readOnly}
                    onChange={(event) => setField("customAgreementReference", event.target.checked ? "" : null)}
                  />
                }
                label="Custom agreement"
              />
              <FieldNote>Holds the order for staff confirmation before billing setup can start.</FieldNote>
            </Box>
            {customAgreementAttached && (
              <Stack spacing={0.5}>
                <FieldLabel htmlFor="custom-agreement-reference" size="small">
                  {customAgreementReferenceLabel}
                </FieldLabel>
                <TextField
                  id="custom-agreement-reference"
                  size="small"
                  fullWidth
                  placeholder={customAgreementReferencePlaceholder}
                  value={draft.customAgreementReference ?? ""}
                  onChange={(event) => setField("customAgreementReference", event.target.value)}
                  helperText={characterCount(
                    (draft.customAgreementReference ?? "").length,
                    customAgreementReferenceMaxLength
                  )}
                  disabled={readOnly}
                  slotProps={{ htmlInput: { maxLength: customAgreementReferenceMaxLength } }}
                />
                <FieldNote>{customAgreementReferenceNote}</FieldNote>
              </Stack>
            )}
            <Divider />
            <SpecialLicenceTermsFields
              draft={draft}
              setField={setField}
              readOnly={readOnly}
              showTermProblems={showTermProblems}
            />
          </Stack>
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <Stack spacing={0.5}>
            <FieldLabel htmlFor="terms-preview" size="small">
              Terms preview
            </FieldLabel>
            <TextField
              id="terms-preview"
              size="small"
              fullWidth
              multiline
              minRows={collapsedPreviewRows}
              maxRows={previewExpanded ? undefined : collapsedPreviewRows}
              value={previewText}
              disabled
            />
            {previewIsLong && (
              <Box sx={{ textAlign: "right" }}>
                <Button size="small" onClick={() => setPreviewExpanded(!previewExpanded)}>
                  {previewExpanded ? "Show less" : "Show more"}
                </Button>
              </Box>
            )}
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}

export function CommercialTermsSection(props: CommercialTermsSectionProps) {
  const { order, draft, setField, readOnly, showTermProblems } = props;
  const sectionProps: SectionProps = { order, draft, setField, readOnly };
  const { data: salesReps, error: salesRepsError } = useAsyncAbortableRequest(loadSalesReps);

  const repOptions = [...new Set([...(salesReps ?? []), ...(draft.salesRep === null ? [] : [draft.salesRep])])].sort();

  return (
    <BuilderSection title="Commercial terms">
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, sm: 7 }}>
          <AccountPanel order={order} />
        </Grid>
        <Grid size={{ xs: 12, sm: 5 }}>
          <Stack spacing={0.5} sx={{ alignItems: "flex-end" }}>
            <FieldLabel id="sales-rep-label" size="small">
              Sales rep
            </FieldLabel>
            <TextField
              id="sales-rep"
              select
              size="small"
              fullWidth
              sx={{ maxWidth: 240 }}
              value={draft.salesRep ?? selfServeValue}
              onChange={(event) =>
                setField("salesRep", event.target.value === selfServeValue ? null : event.target.value)
              }
              helperText={salesRepsError === undefined ? undefined : salesRepsUnavailable}
              slotProps={{ select: { displayEmpty: true, labelId: "sales-rep-label" } }}
              disabled={readOnly || salesRepsError !== undefined}
            >
              <MenuItem value={selfServeValue}>
                <em>{selfServeLabel}</em>
              </MenuItem>
              {repOptions.map((rep) => (
                <MenuItem key={rep} value={rep}>
                  {salesRepDisplayName(rep)}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <ReadOnlyField label="Motion">{order.salesMotion}</ReadOnlyField>
        </Grid>
      </Grid>

      <Stack spacing={3.5} useFlexGap>
        <Divider />
        <QuoteExpiryFields {...sectionProps} />

        <Divider />
        <SubscriptionFields {...sectionProps} />

        <Divider />
        <Stack spacing={0.5}>
          <FieldLabel id="payment-terms-label">Payment terms</FieldLabel>
          <TextField
            id="payment-terms"
            select
            size="small"
            value={draft.paymentTerm}
            onChange={(event) => {
              const term = toPaymentTerm(event.target.value);
              if (term !== undefined) {
                setField("paymentTerm", term);
              }
            }}
            slotProps={{ select: { labelId: "payment-terms-label" } }}
            disabled={readOnly}
            sx={{ maxWidth: 200 }}
          >
            {Object.values(OrderPaymentTerm).map((term) => (
              <MenuItem key={term} value={term}>
                {orderPaymentTermLabels[term]}
              </MenuItem>
            ))}
          </TextField>
        </Stack>

        <Divider />
        <PurchaseOrderFields {...sectionProps} />

        <Divider />
        <LegalTermsFields draft={draft} setField={setField} readOnly={readOnly} showTermProblems={showTermProblems} />
      </Stack>
    </BuilderSection>
  );
}
